import { useEffect, type ReactNode } from 'react';
import { useGetCurrentUserQuery } from './authApi';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  authLoading,
  authSuccess,
  authForbidden,
  authError,
} from './authSlice';
import Spinner from '../../components/Spinner';
import { isLocalDev } from '../../lib/apiClient';

interface AuthBootstrapProps {
  children: ReactNode;
}

/**
 * Wraps the app tree. On mount, calls GetCurrentUser to resolve identity from
 * the Midway/SigV4 session. Until resolved, renders a loading state. If the
 * caller is not a PI team member (403), shows an access-denied screen.
 */
export default function AuthBootstrap({ children }: AuthBootstrapProps) {
  const dispatch = useAppDispatch();
  const { data, error, isLoading, isError } = useGetCurrentUserQuery();
  const status = useAppSelector((s) => s.auth.status);

  useEffect(() => {
    if (isLoading) {
      dispatch(authLoading());
    }
  }, [isLoading, dispatch]);

  useEffect(() => {
    if (data) {
      dispatch(authSuccess(data));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (isError && error) {
      const apiErr = error as { status?: number; data?: { message?: string } };
      if (apiErr.status === 401) {
        // No session (unauthenticated): kick off SSO. The broker's /login redirects
        // to Cognito -> Federate and back to /callback, which sets the pisid cookie
        // and returns here authenticated. Full-page nav (not client routing) because
        // /login is served by CloudFront -> the broker, not the SPA. Not local dev
        // (there the dev proxy injects an alias header, so 401 shouldn't occur).
        if (!isLocalDev) {
          window.location.assign('/login');
          return;
        }
        dispatch(authError(apiErr.data?.message ?? 'Not authenticated.'));
      } else if (apiErr.status === 403) {
        // Authenticated but not a PI member — do NOT redirect to /login (they are
        // already signed in; it would loop). Show the Access Denied screen.
        dispatch(
          authForbidden(
            apiErr.data?.message ?? 'You are not a PI team member.',
          ),
        );
      } else {
        dispatch(authError(apiErr.data?.message ?? 'Failed to authenticate.'));
      }
    }
  }, [isError, error, dispatch]);

  // Gate on the STORE status, not on RTK Query's `data`. `authSuccess` is
  // dispatched from an effect, so `data` becomes truthy one commit BEFORE the
  // slice says 'authenticated'. Rendering children on `data` alone let
  // ProtectedRoute observe a stale 'loading' status and redirect to
  // /auth-error — and because child effects run before parent effects, the
  // <Navigate> won before authSuccess landed. This is the authoritative gate:
  // children mount only once the store agrees the user is authenticated.
  if (status === 'authenticated') {
    return <>{children}</>;
  }

  const apiErr = (isError ? error : undefined) as
    | { status?: number; data?: { message?: string } }
    | undefined;

  // Still resolving, or the 401 effect is navigating to the SSO broker: show the
  // spinner rather than flashing an error card.
  if (isLoading || !apiErr || (apiErr.status === 401 && !isLocalDev)) {
    return <Spinner fullPage label="Authenticating…" />;
  }

  if (apiErr.status === 403) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="card bg-base-100 shadow-xl max-w-md">
          <div className="card-body text-center">
            <h2 className="card-title justify-center text-error">
              Access Denied
            </h2>
            <p className="text-base-content/70">
              You are not a member of the PI team. Portal access requires being
              in the PI organization (reporting to Prasad). If you believe this
              is an error, contact your manager.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="card bg-base-100 shadow-xl max-w-md">
        <div className="card-body text-center">
          <h2 className="card-title justify-center text-error">
            Authentication Error
          </h2>
          <p className="text-base-content/70">
            {apiErr.data?.message ??
              'Unable to verify your identity. Please try again.'}
          </p>
          <button
            className="btn btn-primary mt-4"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}
